import { useEffect, useState } from "react";
import { api } from "../api";

/**
 * Invoice identities, on the Settings page (super admin only): the "FROM"
 * block at the top of an invoice, written once and reused by every client.
 *
 * One is marked default and bills every client that hasn't been pointed at a
 * specific one, resolved when the invoice is generated — so switching the
 * default moves everyone at once. An invoice already issued keeps the lines it
 * was issued with, whatever happens here afterwards.
 */
const BLANK = { name: "", text: "", is_default: false };

export default function InvoiceIdentitiesPanel() {
  const [identities, setIdentities] = useState([]);
  const [editing, setEditing] = useState(null); // identity id, or "new"
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/invoice-identities");
      setIdentities(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't load invoice identities");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const startNew = () => {
    setForm(BLANK);
    setEditing("new");
    setErr(null);
  };

  const startEdit = (i) => {
    setForm({ name: i.name || "", text: i.text || "", is_default: !!i.is_default });
    setEditing(i.id);
    setErr(null);
  };

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      const payload = { name: form.name, text: form.text, is_default: form.is_default };
      if (editing === "new") await api.post("/invoice-identities", payload);
      else await api.patch(`/invoice-identities/${editing}`, payload);
      setEditing(null);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't save the identity");
    } finally {
      setBusy(false);
    }
  };

  const makeDefault = async (i) => {
    setBusy(true);
    setErr(null);
    try {
      await api.patch(`/invoice-identities/${i.id}`, {
        name: i.name,
        text: i.text,
        is_default: true,
      });
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't change the default");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (i) => {
    setBusy(true);
    setErr(null);
    try {
      await api.delete(`/invoice-identities/${i.id}`);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't delete the identity");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card pad" style={{ maxWidth: 640 }}>
      <h3 style={{ marginTop: 0 }}>Invoice identities</h3>
      <p className="muted" style={{ marginTop: 6 }}>
        The "From" block at the top of an invoice. The default is used for every
        client unless one is pointed at another identity on their Users entry.
      </p>

      {err && <div className="error" style={{ marginTop: 12 }}>{err}</div>}

      {identities.length === 0 && editing === null && (
        <p className="muted-sm" style={{ marginTop: 14 }}>No identities yet.</p>
      )}

      {identities.length > 0 && (
        <div style={{ marginTop: 14 }}>
          {identities.map((i) => (
            <div
              key={i.id}
              className="row-h"
              style={{
                justifyContent: "space-between",
                gap: 12,
                padding: "10px 0",
                borderTop: "1px solid var(--line)",
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div className="row-h" style={{ gap: 8 }}>
                  <span style={{ fontWeight: 500 }}>{i.name}</span>
                  {i.is_default && <span className="pill pill-add">default</span>}
                </div>
                <div className="muted-sm" style={{ whiteSpace: "pre-line" }}>
                  {(i.text || "").split("\n").slice(0, 2).join("\n")}
                </div>
              </div>
              <div className="row-h" style={{ gap: 6, flexWrap: "nowrap", flexShrink: 0 }}>
                {!i.is_default && (
                  <button className="ghost" onClick={() => makeDefault(i)} disabled={busy}>
                    Make default
                  </button>
                )}
                <button className="ghost" onClick={() => startEdit(i)} disabled={busy}>
                  Edit
                </button>
                <button
                  className="ghost icon"
                  title="Delete this identity"
                  onClick={() => remove(i)}
                  disabled={busy}
                >
                  ×
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing === null && (
        <div className="row-h" style={{ justifyContent: "flex-end", marginTop: 16 }}>
          <button className="primary" onClick={startNew} disabled={busy}>
            Add identity
          </button>
        </div>
      )}

      {editing !== null && (
        <div style={{ marginTop: 16, borderTop: "1px solid var(--line)", paddingTop: 16 }}>
          <div>
            <label className="field">Name (internal, never printed)</label>
            <input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Personal"
              disabled={busy}
            />
          </div>
          <div style={{ marginTop: 10 }}>
            <label className="field">Printed as the "From" block</label>
            <textarea
              rows={5}
              value={form.text}
              onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))}
              placeholder={"Muhamed Cicak\nStreet, City\nCountry\nemail@example.com"}
              disabled={busy}
            />
            <div className="muted-sm" style={{ marginTop: 4 }}>
              One line each; the first line prints in bold.
            </div>
          </div>
          <label className="check-row" style={{ marginTop: 12 }}>
            <input
              type="checkbox"
              className="cbx"
              checked={form.is_default}
              onChange={(e) => setForm((f) => ({ ...f, is_default: e.target.checked }))}
              disabled={busy}
            />
            Use this one by default
          </label>

          <div className="row-h" style={{ justifyContent: "flex-end", marginTop: 14, gap: 8 }}>
            <button onClick={() => setEditing(null)} disabled={busy}>Cancel</button>
            <button
              className="primary"
              onClick={save}
              disabled={busy || !form.name.trim() || !form.text.trim()}
            >
              {busy ? "Saving…" : "Save identity"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
